import { saveAs } from "file-saver";
import JSZip from "jszip";
import { jsonToCsvString } from "utils/csv";
import { buildFullMerge, CompareTableColumn, MergedRow } from "./comparisons";

const PRESENT_IN_HEADER = "Present in";

type ExportLabels = { aLabel: string; bLabel: string };
type ExportRow = Record<string, string | number | null>;

const roundForExport = (value: number): number => Number(value.toFixed(3));

const toCell = (value: unknown): string | number | null =>
    value == null
        ? null
        : typeof value === "number"
        ? roundForExport(value)
        : String(value);

const sideHeader = (name: string, label: string): string =>
    `${name} (${label})`;

export const buildCompareCsvStringFromMerged = <TRow>(
    columns: ReadonlyArray<CompareTableColumn<TRow>>,
    rows: ReadonlyArray<MergedRow<TRow>>,
    { aLabel, bLabel }: ExportLabels
): Promise<string> => {
    const results = rows.map((row) => {
        const rep = row.a ?? row.b;
        const out: ExportRow = {};

        for (const col of columns) {
            if (col.kind === "join") {
                out[col.headerName] = rep ? toCell(col.getValue(rep)) : null;
            }
        }

        out[PRESENT_IN_HEADER] =
            row.onlyIn === "a" ? aLabel : row.onlyIn === "b" ? bLabel : "Both";

        for (const col of columns) {
            if (col.kind === "join") {
                continue;
            }
            if (col.kind === "display") {
                out[col.headerName] = rep ? toCell(col.getValue(rep)) : null;
            } else if (col.kind === "compare-text") {
                out[sideHeader(col.headerName, aLabel)] = row.a
                    ? toCell(col.getValue(row.a))
                    : null;
                out[sideHeader(col.headerName, bLabel)] = row.b
                    ? toCell(col.getValue(row.b))
                    : null;
            } else {
                out[sideHeader(col.headerName, aLabel)] = row.a
                    ? toCell(col.getValue(row.a))
                    : null;
                out[sideHeader(col.headerName, bLabel)] = row.b
                    ? toCell(col.getValue(row.b))
                    : null;
            }
        }
        return out;
    });

    return jsonToCsvString(results);
};

export const buildCompareCsvString = <TRow>(
    rowsA: ReadonlyArray<TRow>,
    rowsB: ReadonlyArray<TRow>,
    columns: ReadonlyArray<CompareTableColumn<TRow>>,
    labels: ExportLabels
): Promise<string> => {
    const merged = buildFullMerge(rowsA, rowsB, columns);
    return buildCompareCsvStringFromMerged(columns, merged, labels);
};

export type CompareCsvFile = { name: string; csv: string };

export const downloadCompareCsvZip = async (
    files: ReadonlyArray<CompareCsvFile>,
    zipFileName: string
): Promise<void> => {
    const zip = new JSZip();
    files.forEach(({ name, csv }) => zip.file(`${name}.csv`, csv));
    const blob = await zip.generateAsync({ type: "blob" });
    saveAs(blob, zipFileName);
};

export const buildFileName = (
    labels: ExportLabels,
    extension: "csv" | "zip",
    tag?: string
) => {
    const rawFilename = `nira-export-${labels.aLabel}-vs-${labels.bLabel}${
        tag ? `-${tag}` : ""
    }.${extension}`;

    return rawFilename.replace(/\s/g, "_");
};
